/**
 * customEval logic for the CONTAINER source type
 */
import {gql} from '@apollo/client';
import stringify from 'json-stable-stringify';

import {assert, assertAndContinue} from 'Asserts';
import {prependAOrAn} from 'BenchlingUtil';
import {getNonBatchContainerContents} from 'constants/SampleConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetContainerContents,
  GetContainerContentsVariables,
} from 'notebook/integrations/samples/__generated/GetContainerContents';
import {extractContainerId} from 'notebook/integrations/samples/transfer-table-columns/extractContainerId';
import {
  ContainersColumnDefinition,
  ExistingBioentityColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  CustomResolutionQueryType,
  InventoryTableSourceEntityFromSourceContainerQuery,
  InventoryTableValidateSourceContainerQuery,
  ServerResolutionResult,
  StringResolutionType,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {
  ArrayTypedValue,
  BaseTypedValueType,
  CustomEval,
  CustomEvalResult,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {BioentityTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {ContainersColumnMode} from 'notebook/transfer-tables/TransferTableConstants';
import lruMemoize from 'util/lruMemoize';
import {assertNever} from 'util/Types';

type ContainerContent = ExtractByTypename<
  GetContainerContents['containers'][number],
  'ContainerNode'
>['contents'][number];

type ContainedItem = ExcludeByTypename<ContainerContent['containedItem'], 'BatchNode'>;

interface ContentWithoutBatches extends ContainerContent {
  containedItem: ContainedItem;
}

export const GET_CONTAINER_CONTENTS_QUERY = gql`
  query GetContainerContents($containerIds: [StrictString]!) @skipAudit @engComponent(name: INVENTORY) {
    containers(ids: $containerIds) {
      ... on ContainerNode {
        id
        storableSchema {
          ... on StorableSchemaInterface {
            id
          }
        }
        contents {
          id
          containedItem {
            ... on RegistryItemInterface {
              id
              name
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

type SourceContainerResolutionResult =
  | {typedValue: BioentityTypedValue | ArrayTypedValue}
  | SourceContainerError;
interface SourceContainerError {
  errorCode: SourceContainerErrorCode;
}
enum SourceContainerErrorCode {
  INACCESSIBLE = 'INACCESSIBLE',
  EMPTY = 'EMPTY',
  INVALID_SCHEMA = 'INVALID_SCHEMA',
  HAS_MULTIPLE_CONTENTS = 'HAS_MULTIPLE_CONTENTS',
  HAS_BATCH = 'HAS_BATCH',
}

function isError(
  resolutionResult: SourceContainerResolutionResult
): resolutionResult is SourceContainerError {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'errorCode' in resolutionResult;
}

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doQuery(containerId: string): Promise<GetContainerContents> {
    return await doQueryWithoutCache<GetContainerContents, GetContainerContentsVariables>(
      {query: GET_CONTAINER_CONTENTS_QUERY},
      {variables: {containerIds: [containerId]}}
    );
  },
};

/**
 * Do data fetching for resolving the Source Container and Source Entity columns.
 *
 * If successful, return a BioentityTypedValue representing the container's contents.
 *
 * If there is an error, return the error code.
 */
async function resolveSourceContainer(
  containerId: string,
  containerSchemaId?: string | null,
  allowMultiContentsContainer?: boolean
): Promise<SourceContainerResolutionResult> {
  const data = await stubbableFunctions.doQuery(containerId);

  const container = data.containers[0];
  if (isInaccessible(container)) {
    return {errorCode: SourceContainerErrorCode.INACCESSIBLE};
  }
  if (containerSchemaId != null && containerSchemaId !== container.storableSchema.id) {
    return {errorCode: SourceContainerErrorCode.INVALID_SCHEMA};
  }
  const contents = getNonBatchContainerContents<ContainerContent, ContentWithoutBatches>(container.contents);

  if (contents.length === 0) {
    return {errorCode: SourceContainerErrorCode.EMPTY};
  }

  // TODO(PLAN-1130) Better handling for inaccessible contents
  if (allowMultiContentsContainer) {
    const containedItems = contents.map((content) => content.containedItem);
    return {
      typedValue: {
        type: BaseTypedValueType.ARRAY,
        rawValue: containedItems.map((item) => ({
          type: BaseTypedValueType.BIOENTITY,
          bioentitySummary: {
            id: getAccessibleOrInaccessibleId(item),
            name: isInaccessible(item) ? '(No access)' : item.name,
          },
        })),
      },
    };
  }
  if (contents.length > 1) {
    return {errorCode: SourceContainerErrorCode.HAS_MULTIPLE_CONTENTS};
  }
  const containedItem = contents[0].containedItem;

  // TODO(PLAN-1130) Better handling for inaccessible contents
  return {
    typedValue: {
      type: BaseTypedValueType.BIOENTITY,
      bioentitySummary: {
        id: getAccessibleOrInaccessibleId(containedItem),
        name: isInaccessible(containedItem) ? '(No access)' : containedItem.name,
      },
    },
  };
}

/**
 * Resolve custom resolution queries for Source Container and Source Entity.
 *
 * The two columns' queries fetch the same data and share the same core logic,
 * but they yield different return values and error messages.
 */
export const SourceContainerResolver = {
  validateSourceContainer: async (
    query: InventoryTableValidateSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const resolutionResult = await resolveSourceContainer(
      query.containerId,
      query.containerSchemaId,
      query.allowMultiContentsContainer
    );
    // eslint-disable-next-line benchlint/no-in-operator
    if ('errorCode' in resolutionResult) {
      switch (resolutionResult.errorCode) {
        case SourceContainerErrorCode.INACCESSIBLE: {
          // TODO(BNCH-15696): inaccessible chips will be marked invalid after customEval
          return {
            type: StringResolutionType.CUSTOM,
            error: undefined,
            resolvedValue: {
              typedValue: null,
            },
          };
        }
        case SourceContainerErrorCode.INVALID_SCHEMA:
          assert(
            query.containerSchemaName != null,
            `containerSchemaName and containerSchemaId should not be null if error code ${SourceContainerErrorCode.INVALID_SCHEMA} is returned`
          );
          return {
            type: undefined,
            error: `Container must be ${prependAOrAn(query.containerSchemaName)}`,
            resolvedValue: undefined,
          };
        case SourceContainerErrorCode.EMPTY:
          if (query.allowEmptyContainer) {
            return {
              type: StringResolutionType.CUSTOM,
              error: undefined,
              resolvedValue: {
                typedValue: null,
              },
            };
          }
          return {
            type: undefined,
            error: 'Container must not be empty',
            resolvedValue: undefined,
          };
        case SourceContainerErrorCode.HAS_MULTIPLE_CONTENTS:
          return {
            type: undefined,
            error: 'Container has multiple contents',
            resolvedValue: undefined,
          };
        case SourceContainerErrorCode.HAS_BATCH:
          return {
            type: undefined,
            error: 'Contents must be an entity, not a batch',
            resolvedValue: undefined,
          };
        default:
          throw new Error(`Unexpected error type ${assertNever(resolutionResult.errorCode)}`);
      }
    }
    return {
      type: StringResolutionType.CUSTOM,
      error: undefined,
      resolvedValue: {
        typedValue: null,
      },
    };
  },

  autopopulateSourceEntity: async (
    query: InventoryTableSourceEntityFromSourceContainerQuery
  ): Promise<ServerResolutionResult> => {
    const resolutionResult = await resolveSourceContainer(
      query.containerId,
      query.containerSchemaId,
      query.allowMultiContentsContainer
    );
    if (isError(resolutionResult)) {
      // The source container is invalid.
      // To fix the error, the user must fix the Source Container column.
      // For the Source Entity column, we shouldn't return an error. It wouldn't be actionable.
      return {
        type: StringResolutionType.CUSTOM,
        error: undefined,
        resolvedValue: {
          typedValue: null,
        },
      };
    } else {
      return {
        type: StringResolutionType.CUSTOM,
        error: undefined,
        resolvedValue: resolutionResult,
      };
    }
  },
};

export function sourceContainerCustomEval(
  typedValue: TypedValue,
  columnDefinition: ContainersColumnDefinition,
  cellDependencies: [],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
  // would like to operate on a post-resolved value, so we manually fire it off first.
  const {resolvedValue: resolvedTypedValue, error: resolutionError} = resolveTypedValueToType(
    columnDefinition,
    typedValue
  );
  if (resolutionError || resolvedTypedValue == null) {
    return {val: resolvedTypedValue ?? typedValue, error: resolutionError, locked: false};
  }

  const containerId = extractContainerId(resolvedTypedValue);
  if (!containerId) {
    assertAndContinue(
      !columnDefinition.isRequired,
      'Expected string resolution to either provide a valid container, or error and early return',
      {typedValue, resolvedTypedValue}
    );
    return {val: resolvedTypedValue, error: null, locked: false};
  }

  const {resolvedValue: customResolvedValue, error} = resolveFromResolutionQuery(
    resolvedTypedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.INVENTORY_TABLE_VALIDATE_SOURCE_CONTAINER,
      type: ResolutionQueryType.CUSTOM,
      containerId,
      ...(columnDefinition.containersColumnMode === ContainersColumnMode.INVENTORY_CONTAINER && {
        allowEmptyContainer: true,
        allowMultiContentsContainer: true,
        containerSchemaId: columnDefinition.containerSchemaId ?? null,
        containerSchemaName: columnDefinition.containerSchemaName ?? null,
      }),
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: customResolvedValue, error, locked: false};
}

export const getSourceEntityCustomEval = lruMemoize(_getSourceEntityCustomEval, {max: 100}, (...args) =>
  stringify(args)
);

function _getSourceEntityCustomEval<CustomEvalOptions>({
  lockIfContainerValIsEmpty,
  containerSchemaId,
  allowMultiContentsContainer,
}: {
  lockIfContainerValIsEmpty: boolean;
  containerSchemaId?: string | null;
  allowMultiContentsContainer?: boolean;
}): CustomEval<CustomEvalOptions> {
  return (
    typedValue: TypedValue,
    columnDefinition: ExistingBioentityColumnDefinition,
    cellDependencies: [TypedValue | null],
    customEvalOptions: unknown,
    resolveFromResolutionQuery: ResolveQueryFn
  ) => {
    const containerId = extractContainerId(cellDependencies[0]);
    if (!containerId) {
      return {
        val: {type: BaseTypedValueType.STRING, rawValue: ''},
        error: null,
        locked: lockIfContainerValIsEmpty,
      };
    }

    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        customResolutionQueryType:
          CustomResolutionQueryType.INVENTORY_TABLE_SOURCE_ENTITY_FROM_SOURCE_CONTAINER,
        containerId,
        containerSchemaId,
        ...(allowMultiContentsContainer != null && {allowMultiContentsContainer}),
        type: ResolutionQueryType.CUSTOM,
      },
      makeTypedValueFromCustomResolvedValue
    );
    return {val: resolvedValue, error, locked: true};
  };
}
